import {useEffect, useRef, useState} from "react";
import {boardDrinkSummary, drinkEventText, recentDrinkEvents} from "../public/js/game/drinkEvents.js";
import {seatMeta, tallyGroups} from "../public/js/game/boardLayout.js";
import {boardDrinkLines, buildBoardHistory, nextCardAnimation} from "../public/js/game/eventLog.js";
import {cardShowScript, fatigueStamps, heldPlayIds, presentedSeats, SHOW_MAX_AGE_MS} from "../public/js/game/cardShow.js";
import {
  bottleAim,
  bottleRing,
  bottleSpin,
  coinFrame,
  diceFace,
  dicePips,
  duelFaces,
  GAMBLE_HOLD_MS,
  GAMBLE_ROLL_MS,
  rouletteFrame,
  wheelFrame,
  wheelPointer,
} from "../public/js/game/gambleShow.js";
import {artClass, initials, numberText} from "../public/js/utils/sanitize.js";
import {randomInt} from "../public/js/utils/identity.js";
import {useAlertExpiry, useElementSize} from "./useGame.js";
import {KickButton} from "./KickButton.jsx";
import {TurnClock} from "./TurnClock.jsx";
import {MinigameTable} from "./MinigameTable.jsx";

// Keeping every id a client has ever animated leaks across a long session.
const MAX_SEEN_CARD_EVENTS = 64;
// One card show, beat by beat in ms from its start: the player's mat lifts, the card
// leaps off it and hangs over the bar, slides to every seat it touched, lands, and
// clears the bar for the next one. The players asked for about two and a half seconds.
const SHOW = {leap: 110, rise: 680, slide: 1480, land: 1960, end: 2500};
const SETTLE = "cubic-bezier(0.16, 1, 0.3, 1)";
// The leap travels the whole way at a pace the room can follow, rather than arriving
// in its first frames the way an exponential ease-out does.
const CARRY = "cubic-bezier(0.45, 0.05, 0.25, 1)";

function calmMotion() {
  return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
}

// Everything the show paints is built with createElement and textContent: card names,
// player names and stamps all came off a blob anyone with the code can POST.
function el(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text) node.textContent = text;
  return node;
}

function spawnEffect(layer, className, vars, lifetime, textContent = "") {
  const effect = el("div", className, textContent);
  for (const [name, value] of Object.entries(vars)) effect.style.setProperty(name, value);
  layer.appendChild(effect);
  setTimeout(() => effect.remove(), lifetime);
  return effect;
}

// The card as its player holds it up: art, name, its own text, and along its foot who
// threw it at whom. Its effect tints the frame.
function showCard(script) {
  const card = el("div", `show-card tone-${script.effect}`);
  const face = el("div", "show-card-face");
  const body = el("div", "show-card-body");
  body.append(el("strong", "show-name", script.name));
  if (script.text) body.append(el("p", "show-text", script.text));
  face.append(el("span", `${artClass(script.image)} show-art`), body);
  if (script.byline) face.append(el("p", "show-byline", script.byline));
  card.append(face);
  return card;
}

export function Board({controller, snapshot}) {
  const {game, view, playerId} = snapshot;
  const canKick = controller.canKickPlayers();
  const arenaRef = useRef(null);
  const abilityRef = useRef(null);
  const playersRef = useRef(null);
  const seenCardEvents = useRef(new Set());
  const seenFatigue = useRef(new Set());
  const timers = useRef([]);
  const frame = useRef(null);
  // The play on screen, and whether its card has landed yet: until it has, the seats it
  // is headed for still show the numbers from before it was played.
  const [showing, setShowing] = useState(null);
  const arenaSize = useElementSize(arenaRef);

  useEffect(() => () => {
    timers.current.forEach(clearTimeout);
    cancelAnimationFrame(frame.current);
  }, []);

  function later(ms, fn) {
    timers.current.push(setTimeout(fn, ms));
  }

  function centerOf(playerIndex) {
    const playerEl = playersRef.current?.querySelector(`[data-player="${playerIndex}"]`);
    if (!playerEl || !arenaRef.current || !abilityRef.current) return null;
    const arenaBox = arenaRef.current.getBoundingClientRect();
    const targetBox = playerEl.getBoundingClientRect();
    return {
      element: playerEl,
      x: targetBox.left + targetBox.width / 2 - arenaBox.left,
      y: targetBox.top + targetBox.height / 2 - arenaBox.top,
    };
  }

  // Out: the mat turns over (the class does that) and the glass goes everywhere — a
  // wider, heavier splash than any hit, and a shower of glass.
  function eliminationBurst(deadPlayerId) {
    const playerIndex = game.players.findIndex((player) => player.id === deadPlayerId);
    const center = centerOf(playerIndex);
    if (!center || center.element.dataset.eliminating === "true") return;
    center.element.dataset.eliminating = "true";
    splash(center, "damage", 26, [90, 190]);
    for (let i = 0; i < 14; i += 1) {
      spawnEffect(abilityRef.current, "glass-shard", {
        "--x": `${center.x + randomInt(-40, 40)}px`,
        "--y": `${center.y + randomInt(-30, 30)}px`,
        "--dx": `${randomInt(-140, 140)}px`,
        "--dy": `${randomInt(-110, 0)}px`,
        "--r": `${randomInt(-320, 320)}deg`,
      }, 900);
    }
    setTimeout(() => {
      delete center.element.dataset.eliminating;
    }, 5200);
  }

  // Carbonation off the card while it hangs over the bar: bubbles rise and pop.
  function fizz(middle) {
    for (let i = 0; i < 16; i += 1) {
      spawnEffect(abilityRef.current, "fizz", {
        "--x": `${middle.x + randomInt(-170, 170)}px`,
        "--y": `${middle.y + randomInt(40, 150)}px`,
        "--size": `${randomInt(8, 20)}px`,
        "--rise": `${randomInt(90, 190)}px`,
        "--delay": `${randomInt(0, 760)}ms`,
      }, 1800);
    }
  }

  // The wet streak a glass leaves on lacquer, laid along the card's true heading.
  function streak(from, to) {
    const length = Math.hypot(to.x - from.x, to.y - from.y);
    spawnEffect(abilityRef.current, "slide-streak", {
      "--x": `${from.x}px`,
      "--y": `${from.y}px`,
      "--length": `${length}px`,
      "--angle": `${Math.atan2(to.y - from.y, to.x - from.x)}rad`,
    }, 900);
  }

  function splash(at, tone, drops = 12, [near, far] = [56, 118]) {
    for (let i = 0; i < drops; i += 1) {
      const angle = (i / drops) * Math.PI * 2 + Math.random() * 0.4;
      const reach = randomInt(near, far);
      spawnEffect(abilityRef.current, `splash-drop tone-${tone}`, {
        "--x": `${at.x}px`,
        "--y": `${at.y}px`,
        "--dx": `${Math.cos(angle) * reach}px`,
        "--dy": `${Math.sin(angle) * reach}px`,
        "--size": `${randomInt(7, 14)}px`,
      }, 760);
    }
  }

  // A guard that falls breaks where it stood. Read before the landing re-render takes
  // its token off the coaster.
  function shatter(seatElement, minionId) {
    const token = [...seatElement.querySelectorAll("[data-minion]")].find((node) => node.dataset.minion === minionId);
    if (!token) return;
    const arenaBox = arenaRef.current.getBoundingClientRect();
    const box = token.getBoundingClientRect();
    for (let i = 0; i < 10; i += 1) {
      spawnEffect(abilityRef.current, "glass-shard", {
        "--x": `${box.left + box.width / 2 - arenaBox.left}px`,
        "--y": `${box.top + box.height / 2 - arenaBox.top}px`,
        "--dx": `${randomInt(-70, 70)}px`,
        "--dy": `${randomInt(-60, 10)}px`,
        "--r": `${randomInt(-260, 260)}deg`,
      }, 900);
    }
  }

  // The stamp lands beside the mat, on the side the card came in from, so the room
  // watches the count bump and the tally tick underneath it rather than behind it.
  function stampAt(at, stop) {
    const arena = arenaRef.current.getBoundingClientRect();
    const mat = at.element.getBoundingClientRect();
    const toward = {x: arena.width / 2 - at.x, y: arena.height / 2 - at.y};
    const length = Math.hypot(toward.x, toward.y) || 1;
    const unit = {x: toward.x / length, y: toward.y / length};
    const reach = Math.abs(unit.x) * (mat.width / 2 + 70) + Math.abs(unit.y) * (mat.height / 2 + 48);
    const clamp = (value, edge, size) => Math.max(edge, Math.min(size - edge, value));
    const stampEl = spawnEffect(abilityRef.current, `impact-stamp tone-${stop.tone}`, {
      "--x": `${clamp(at.x + unit.x * reach, 110, arena.width)}px`,
      "--y": `${clamp(at.y + unit.y * reach, 56, arena.height)}px`,
      "--tilt": `${randomInt(-9, -3)}deg`,
    }, 1500);
    stampEl.append(el("strong", "", stop.big));
    stop.lines.forEach((line) => stampEl.append(el("span", "", line)));
  }

  function land(script) {
    script.stops.forEach((stop) => {
      const at = centerOf(stop.playerIndex);
      if (!at) return;
      stop.fallen.forEach((minionId) => shatter(at.element, minionId));
      // The ring runs round the mat's own edge, never across the count it is changing.
      const mat = at.element.getBoundingClientRect();
      spawnEffect(abilityRef.current, `effect ${stop.tone}`, {
        "--x": `${at.x}px`,
        "--y": `${at.y}px`,
        "--w": `${mat.width}px`,
        "--h": `${mat.height}px`,
      }, 760);
      splash(at, stop.tone);
      if (stop.big) stampAt(at, stop);
      // Not a React prop: the seat re-renders as the landing lands, and the shake has
      // to survive that. Cleared before the next card can arrive.
      at.element.dataset.impact = stop.tone;
      setTimeout(() => delete at.element.dataset.impact, 640);
    });
    setShowing((current) => (current?.id === script.playId ? {...current, landed: true} : current));
  }

  function slide(script, card, middle, calm) {
    const targets = script.stops.map((stop) => centerOf(stop.playerIndex)).filter(Boolean);
    const duration = SHOW.land - SHOW.slide;
    if (!targets.length || calm) {
      card.animate([{opacity: 1}, {opacity: 0}], {duration, easing: "ease-in", fill: "forwards"});
      return;
    }
    // One seat: the card itself slides there. Several: it deals itself out in copies.
    const travellers = targets.length === 1 ? [card] : targets.map(() => {
      const copy = card.cloneNode(true);
      copy.classList.add("show-copy");
      abilityRef.current.appendChild(copy);
      later(duration + 40, () => copy.remove());
      return copy;
    });
    if (travellers[0] !== card) card.animate([{opacity: 1}, {opacity: 0}], {duration: 160, fill: "forwards"});
    travellers.forEach((traveller) => traveller.classList.add("sliding"));
    targets.forEach((to, i) => {
      const dx = to.x - middle.x;
      const dy = to.y - middle.y;
      const tilt = Math.max(-18, Math.min(18, dx / 24));
      streak(middle, to);
      travellers[i].animate([
        {transform: "translate(0, 0) scale(1) rotate(-1.5deg)", opacity: 1},
        {transform: `translate(${dx * 0.12}px, ${dy * 0.12}px) scale(0.86) rotate(${tilt / 2}deg)`, opacity: 1, offset: 0.25},
        {transform: `translate(${dx}px, ${dy}px) scale(0.26) rotate(${tilt}deg)`, opacity: 1, offset: 0.92},
        {transform: `translate(${dx}px, ${dy}px) scale(0.2) rotate(${tilt}deg)`, opacity: 0},
      ], {duration, easing: "cubic-bezier(0.55, 0, 0.25, 1)", fill: "forwards"});
    });
  }

  function seatElement(playerIndex) {
    return playersRef.current?.querySelector(`[data-player="${playerIndex}"]`) || null;
  }

  // The thing the room watches while a chance card decides: built once, then moved frame
  // by frame off the same functions the phones use, so the TV and a phone land alike.
  // update() answers a key that changes on every face, hop or field, which is what ticks.
  function gambleProp(gamble) {
    if (gamble.kind === "coin") {
      const coin = el("div", "gamble-coin");
      const krona = el("span", "coin-face krona", "♛");
      krona.append(el("small", "", "Krona"));
      const klave = el("span", "coin-face klave", "1");
      klave.append(el("small", "", "Klave"));
      coin.append(krona, klave);
      return {
        node: coin,
        update(elapsed) {
          const {angle, lift} = coinFrame(elapsed, gamble.face);
          coin.style.transform = `translateY(${-lift * 110}px) rotateX(${angle}deg)`;
          return Math.floor(angle / 180);
        },
      };
    }
    if (gamble.kind === "dice") {
      const die = el("div", "gamble-die");
      const pips = Array.from({length: 9}, () => el("i"));
      die.append(...pips);
      return {
        node: die,
        update(elapsed) {
          const face = diceFace(elapsed, gamble.sides, gamble.value);
          dicePips(face).forEach((on, cell) => pips[cell].classList.toggle("on", on));
          return face;
        },
      };
    }
    if (gamble.kind === "duel") {
      const duel = el("div", "gamble-duel");
      const sides = gamble.values.map((_, side) => {
        const box = el("div", "duel-side");
        const die = el("div", "gamble-die");
        const pips = Array.from({length: 9}, () => el("i"));
        die.append(...pips);
        box.append(die, el("span", "duel-name", gamble.names[side]));
        return {box, pips};
      });
      duel.append(sides[0].box, el("span", "duel-versus", "mot"), sides[1].box);
      return {
        node: duel,
        update(elapsed) {
          const faces = duelFaces(elapsed, gamble.values);
          faces.forEach((die, side) => {
            dicePips(die.face).forEach((on, cell) => sides[side].pips[cell].classList.toggle("on", on));
            sides[side].box.classList.toggle("settled", !die.rolling);
            sides[side].box.classList.toggle("lost", elapsed >= GAMBLE_ROLL_MS && gamble.loser === side);
          });
          return faces.map((die) => die.face).join(":");
        },
      };
    }
    if (gamble.kind === "bottle") {
      const pivot = el("div", "gamble-bottle-pivot");
      const bottle = el("div", "gamble-bottle");
      bottle.append(el("span", "bottle-neck"), el("span", "bottle-body"));
      pivot.append(bottle);
      const pickSeat = gamble.seats[gamble.landings[0]];
      // Aimed at the victim's real mat on this screen, measured as the spin starts; a
      // victim who has left is pointed at round an imagined ring instead.
      let final = null;
      const aim = () => {
        const at = centerOf(pickSeat);
        const box = arenaRef.current.getBoundingClientRect();
        return at ? bottleAim(at.x - box.width / 2, at.y - box.height / 2) : bottleRing(gamble.landings[0] || 0, gamble.seats.length || 1);
      };
      return {
        node: pivot,
        update(elapsed) {
          if (final === null) final = aim();
          const angle = bottleSpin(elapsed, final);
          pivot.style.transform = `translate(-50%, -50%) rotate(${angle}deg)`;
          const seat = seatElement(pickSeat);
          if (seat && elapsed >= GAMBLE_ROLL_MS) seat.dataset.spot = "picked";
          return Math.floor(angle / 30);
        },
        clear() {
          const seat = seatElement(pickSeat);
          if (seat) delete seat.dataset.spot;
        },
      };
    }
    if (gamble.kind === "wheel") {
      const wrap = el("div", "gamble-wheel-wrap");
      const wheel = el("div", "gamble-wheel");
      wheel.style.setProperty("--count", gamble.segments.length);
      const labels = gamble.segments.map((label, index) => {
        const span = el("span", "", label);
        span.style.setProperty("--i", index);
        return span;
      });
      wheel.append(...labels);
      wrap.append(wheel, el("span", "gamble-pointer"));
      return {
        node: wrap,
        update(elapsed) {
          const angle = wheelFrame(elapsed, gamble.index, gamble.segments.length);
          wheel.style.transform = `rotate(${angle}deg)`;
          const under = wheelPointer(angle, gamble.segments.length);
          labels.forEach((label, index) => label.classList.toggle("hit", elapsed >= GAMBLE_ROLL_MS && index === under));
          return under;
        },
      };
    }
    // The roulette has no prop: the spotlight runs round the players' own mats.
    return {
      node: null,
      update(elapsed) {
        const {spot, locked} = rouletteFrame(elapsed, gamble.seats.length, gamble.landings);
        gamble.seats.forEach((playerIndex, index) => {
          const seat = seatElement(playerIndex);
          if (!seat) return;
          if (locked.includes(index)) seat.dataset.spot = "picked";
          else if (spot === index) seat.dataset.spot = "on";
          else delete seat.dataset.spot;
        });
        return spot;
      },
      clear() {
        gamble.seats.forEach((playerIndex) => {
          const seat = seatElement(playerIndex);
          if (seat) delete seat.dataset.spot;
        });
      },
    };
  }

  // A chance card's moment, between the card arriving over the bar and sliding off it:
  // the caption, the coin or die or wheel or spotlight, a tick on every hop, then the
  // verdict stamped in the middle with a fanfare or a sad trombone. Under reduced motion
  // nothing spins: the caption waits, and the result simply appears.
  function runGamble(gamble, middle, calm) {
    const layer = abilityRef.current;
    if (!layer) return;
    const stage = el("div", `gamble-stage kind-${gamble.kind}${calm ? " calm" : ""}`);
    stage.append(el("p", "gamble-caption", gamble.caption));
    const prop = gambleProp(gamble);
    if (prop.node) stage.append(prop.node);
    // The verdict's place is kept from the start, so the prop does not jump when it lands.
    const slot = el("div", "gamble-slot");
    stage.append(slot);
    layer.appendChild(stage);
    const started = performance.now();
    let lastKey = prop.update(0);
    const step = () => {
      if (!stage.isConnected) return;
      const elapsed = Math.min(GAMBLE_ROLL_MS, performance.now() - started);
      const key = prop.update(elapsed);
      if (key !== lastKey) controller.soundBoard.playCue("tick");
      lastKey = key;
      if (elapsed < GAMBLE_ROLL_MS) frame.current = requestAnimationFrame(step);
    };
    if (!calm) frame.current = requestAnimationFrame(step);
    later(GAMBLE_ROLL_MS, () => {
      prop.update(GAMBLE_ROLL_MS);
      stage.classList.add("landed");
      const verdict = el("div", `gamble-verdict tone-${gamble.tone}`);
      verdict.append(el("strong", "", gamble.headline), el("span", "", gamble.detail));
      slot.append(verdict);
      controller.soundBoard.playCue(gamble.tone === "lose" ? "lose" : "win");
      if (calm) return;
      if (gamble.tone === "lose") {
        splash(middle, "damage", 22, [80, 200]);
        return;
      }
      for (let i = 0; i < 16; i += 1) {
        spawnEffect(layer, "firework", {
          "--x": `${randomInt(28, 72)}%`,
          "--y": `${randomInt(14, 62)}%`,
          "--delay": `${randomInt(0, 500)}ms`,
          "--hue": `${randomInt(0, 359)}deg`,
        }, 2600);
      }
    });
    later(GAMBLE_ROLL_MS + GAMBLE_HOLD_MS, () => {
      stage.classList.add("leaving");
      prop.clear?.();
    });
    later(GAMBLE_ROLL_MS + GAMBLE_HOLD_MS + 380, () => stage.remove());
  }

  function runShow(script) {
    const layer = abilityRef.current;
    const box = arenaRef.current.getBoundingClientRect();
    const middle = {x: box.width / 2, y: box.height / 2};
    const origin = centerOf(script.shooterIndex);
    const from = origin || {x: middle.x, y: box.height};
    const calm = calmMotion();
    const card = showCard(script);
    // The mat lifts first, so the room knows whose card is coming before it comes.
    if (origin) {
      origin.element.dataset.impact = "launch";
      setTimeout(() => delete origin.element.dataset.impact, 520);
    }
    const dx = from.x - middle.x;
    const dy = from.y - middle.y;
    card.animate(calm ? [{opacity: 0}, {opacity: 1}] : [
      {transform: `translate(${dx}px, ${dy}px) scale(0.2) rotate(-14deg)`, opacity: 0},
      {transform: `translate(${dx}px, ${dy - 40}px) scale(0.3) rotate(-10deg)`, opacity: 1, offset: 0.14},
      {transform: `translate(${dx * 0.35}px, ${dy * 0.35 - 60}px) scale(0.78) rotate(-4deg)`, opacity: 1, offset: 0.62},
      {transform: "translate(0, 0) scale(1.05) rotate(1.5deg)", opacity: 1, offset: 0.88},
      {transform: "translate(0, 0) scale(1) rotate(-1.5deg)", opacity: 1},
    ], {duration: SHOW.rise, delay: SHOW.leap, easing: CARRY, fill: "both"});
    layer.appendChild(card);
    if (!calm) later(SHOW.leap + SHOW.rise - 120, () => fizz(middle));
    // A chance card holds the card over the bar while it decides, and everything after
    // the hold waits for it.
    const suspense = script.gamble ? GAMBLE_ROLL_MS + GAMBLE_HOLD_MS : 0;
    if (script.gamble) later(SHOW.leap + SHOW.rise, () => runGamble(script.gamble, middle, calm));
    later(SHOW.slide + suspense, () => slide(script, card, middle, calm));
    later(SHOW.land + suspense, () => land(script));
    later(SHOW.end + suspense, () => {
      card.remove();
      setShowing(null);
    });
  }

  // Card plays synced from other devices are shown once each, oldest first and one at
  // a time: a poll can land several at once, and the board plays them back in order.
  // While one is on screen the next waits; clearing `showing` is what calls it up. The
  // hand view skips the show: the phone has its own alerts.
  useEffect(() => {
    if (view === "hand" || showing || !abilityRef.current) return;
    const event = nextCardAnimation(game.events, seenCardEvents.current, Date.now(), SHOW_MAX_AGE_MS);
    if (!event) return;
    seenCardEvents.current.add(event.id);
    if (seenCardEvents.current.size > MAX_SEEN_CARD_EVENTS) {
      seenCardEvents.current.delete(seenCardEvents.current.values().next().value);
    }
    controller.soundBoard.playCardEvent(event);
    setShowing({id: event.id, landed: false});
    runShow(cardShowScript(game.events, event.id, game.players));
  }, [game.events, view, showing]);

  // An empty deck bites at a turn start, with no card to slide: the bar stamps it on the
  // player's own mat — "Tom lek!" — once whatever show is on the bar has finished.
  useEffect(() => {
    if (view === "hand" || showing || !abilityRef.current) return;
    fatigueStamps(game.events, game.players, seenFatigue.current).forEach((stop) => {
      seenFatigue.current.add(stop.id);
      if (seenFatigue.current.size > MAX_SEEN_CARD_EVENTS) {
        seenFatigue.current.delete(seenFatigue.current.values().next().value);
      }
      const at = centerOf(stop.playerIndex);
      if (!at) return;
      if (!calmMotion()) splash(at, "damage");
      stampAt(at, stop);
      at.element.dataset.impact = "damage";
      setTimeout(() => delete at.element.dataset.impact, 640);
    });
  }, [game.events, view, showing]);

  // The table as the room should see it right now: any play still queued or still in
  // the air has its sips held off the coasters, so nothing moves before its card lands.
  const held = heldPlayIds(game.events, seenCardEvents.current, showing);
  const seats = presentedSeats(game.players, game.events, held);

  const events = recentDrinkEvents(game.events);
  const summary = boardDrinkSummary(events);
  const showBoardAlert = Boolean(events.length) && view !== "hand";
  useAlertExpiry(showBoardAlert ? events[events.length - 1].id : null);

  // Keyed on which deaths are showing — and only once the card that caused one has
  // landed, so the knockout bursts where the stamp says "Ute!", not two seconds early.
  const deaths = showBoardAlert
    ? events.filter((event) => event.type === "death" && seats[game.players.findIndex((player) => player.id === event.playerId)]?.dead)
    : [];
  const deathKey = deaths.map((event) => event.id).join(",");
  useEffect(() => {
    deaths.forEach((event) => eliminationBurst(event.playerId));
  }, [deathKey]);

  // Seat placement, card scale and arena height all come from the controller, which
  // gets them from boardLayout.js — where a test walks every pair of seats at every
  // roster size and arena size and proves the cards never land on top of each other.
  // The arena's measured size is what lets a TV give a full table readable cards.
  const board = controller.boardMetrics(arenaSize);
  const status = controller.boardStatus();
  const current = controller.currentPlayer();
  const activePlayer = game.status === "playing" ? current : null;
  const activeIndex = game.players.indexOf(activePlayer);
  // A play still queued or in the air keeps its ticket off the rail, or the rail would
  // read the result out from under a spinning coin.
  const entries = buildBoardHistory(game.events, 8, held);
  const celebration = controller.winnerCelebration();
  // The evening's honours, laid out as mats under the winner once the match is over.
  const awards = controller.boardAwards();

  // One volley of fireworks when the match ends; keyed on status so re-renders while
  // the winner screen is up do not relaunch it.
  useEffect(() => {
    if (!celebration || !abilityRef.current) return;
    for (let i = 0; i < 26; i += 1) {
      spawnEffect(abilityRef.current, "firework", {
        "--x": `${randomInt(6, 94)}%`,
        "--y": `${randomInt(8, 72)}%`,
        "--delay": `${randomInt(0, 2400)}ms`,
        "--hue": `${randomInt(0, 359)}deg`,
      }, 4200);
    }
  }, [game.status]);

  // App only mounts the board once the lobby is over, so nothing here branches on
  // status === "lobby" any more — the waiting room owns that screen and its buttons.
  return (
    <section className="board" aria-label="Spelplan">
      {/* The back-bar sign. The name and the code are here only so a latecomer can
          find them; the turn is what the room reads off the screen, so it gets the
          weight. */}
      <header className="topbar">
        <div className="board-identity">
          <span className="board-wordmark">SipRoyale</span>
          <span className="board-code">{game.code}</span>
        </div>
        <h2 className="turn-sign">{status.headline}</h2>
        <span className="turn-detail">
          {status.detail}
          <TurnClock controller={controller} compact />
        </span>
      </header>

      {/* Past ten seats the ellipse runs out of vertical room and cards start landing
          on each other, so the arena reflows as a plain grid. The seat coordinates are
          still handed over and simply go unused — the class is what switches it. */}
      <div
        className={board.layout === "grid" ? "arena arena-grid" : "arena"}
        ref={arenaRef}
        style={{"--arena-height": `${board.height}px`, "--seat-scale": board.scale}}
      >
        <div className="ability-layer" aria-hidden="true" ref={abilityRef}></div>
        <div className="board-alert" hidden={!showBoardAlert}>
          {showBoardAlert && (
            <>
              <strong>{summary.title}</strong>
              {summary.events.length > 1 ? (
                <div className="board-alert-list">
                  {summary.events.map((event) => (
                    <span key={event.id}><b>{event.playerName}</b> {drinkEventText(event)}</span>
                  ))}
                </div>
              ) : (
                <span>{drinkEventText(summary.events[0])}</span>
              )}
            </>
          )}
        </div>
        <div className="players" ref={playersRef}>
          {/* The pendant lamp over the bar: a pool of light that follows the turn. */}
          {activeIndex !== -1 && board.layout === "ring" && (
            <div
              className="bar-light"
              aria-hidden="true"
              style={{"--seat-x": `${board.seats[activeIndex].x}%`, "--seat-y": `${board.seats[activeIndex].y}%`}}
            ></div>
          )}
          {game.players.map((player, index) => {
            const shown = seats[index];
            const seat = board.seats[index];
            const classes = [
              "player",
              player.id === playerId ? "me" : "",
              player === activePlayer ? "active" : "",
              shown.dead ? "defeated" : "",
              shown.minions.length ? "guarded" : "",
            ].join(" ");
            return (
              <article
                key={player.id}
                className={classes}
                data-player={index}
                style={{
                  "--seat-x": `${seat.x}%`,
                  "--seat-y": `${seat.y}%`,
                  "--seat-color": seat.color,
                }}
              >
                {/* Icon and name share a row: it reads as one identity, and the height
                    it saves is what lets the minion row sit inside the card. */}
                <div className="player-head">
                  <div className="avatar" title={player.name}>
                    <span aria-hidden="true">{seat.icon}</span>
                  </div>
                  <h3>{player.name}</h3>
                </div>
                {/* The count is keyed on itself so a new number lands with a bump; the
                    tally is the waiter's pencil on the beer mat, five to a gate. */}
                <div className="sips-row">
                  <strong className="sips-count" key={shown.sipsTaken}>{numberText(shown.sipsTaken)}</strong>
                  <span className="sips-max">/{numberText(player.maxSips)}</span>
                  <span className="tally" aria-hidden="true">
                    {tallyGroups(shown.sipsTaken, player.maxSips).map((group) => (
                      <span key={group.key} className="tally-group">
                        {Array.from({length: group.size}, (_, stroke) => (
                          <i key={stroke} className={stroke < group.lit ? "lit" : ""}></i>
                        ))}
                      </span>
                    ))}
                  </span>
                </div>
                <small>{seatMeta(player)}</small>
                {/* In the card's own flow, not floating above it. The old overlay hung
                    30px over the top edge and printed this player's minions across
                    whoever was sitting behind them. Each guard is a glass standing on
                    the front of the coaster, drawn with the card that summoned it. */}
                <div className="minions">
                  {shown.minions.map((minion) => (
                    <span key={minion.id} className="minion-token" data-minion={minion.id}>
                      {minion.image
                        ? <span className={`${artClass(minion.image)} minion-art`}></span>
                        : <strong>{initials(minion.name)}</strong>}
                      <em>{minion.name}</em>
                      <b>{numberText(minion.hp)}/{numberText(minion.maxHp)}</b>
                    </span>
                  ))}
                </div>
                {shown.dead && <span className="seat-out">Ute</span>}
                {canKick && <KickButton controller={controller} player={player} />}
              </article>
            );
          })}
        </div>
        {/* The slip says what happened last, so while plays are still queued or in the
            air it would be telling the room the end of a story the bar has not shown
            yet. It waits for the queue to drain. */}
        <div className={held.length ? "table-center waiting" : "table-center"}>
          <p>{game.log || "Välj ett kort och en motståndare."}</p>
        </div>
        {/* Blackjack or the ballot, dealt over the middle of the bar — after the card
            that started it has slid, like the slip waits for the queue. */}
        <MinigameTable controller={controller} waiting={held.length > 0} />
        {(celebration || awards.length > 0) && (
          <div className={awards.length ? "winner-screen with-awards" : "winner-screen"}>
            {celebration && (
              <div className="winner-card">
                <span className="winner-chicken" aria-hidden="true"></span>
                <h2>{celebration.name}</h2>
                <p className="winner-title">vann</p>
                <p>{game.log}</p>
              </div>
            )}
            {awards.length > 0 && (
              <ul className="award-mats" aria-label="Kvällens priser">
                {awards.map((award, index) => (
                  <li key={award.id} className="award-mat" style={{"--i": index}}>
                    <span className="award-icon" aria-hidden="true">{award.icon}</span>
                    <span className="award-title">{award.title}</span>
                    <strong className="award-who">{award.who}</strong>
                    <span className="award-detail">{award.detail}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {/* The ticket rail: every play's slip hangs off the bar's brass rail, newest
          first, with what it made people drink. */}
      <section className="event-log" aria-label="Senaste händelser">
        <div className="event-log-list">
          {entries.length === 0 ? (
            <p className="event-log-empty">Inga kort har spelats än.</p>
          ) : (
            entries.map((entry) => (
              <article key={entry.id} className="event-log-item">
                <div>
                  <strong>{entry.playerName ? `${entry.playerName} spelade ${entry.cardName}` : entry.cardName}</strong>
                  <p>{entry.cardText || (entry.targetName ? `Mot ${entry.targetName}` : "Ingen extra effekt.")}</p>
                </div>
                {entry.targetName && <span className="event-target">Mot {entry.targetName}</span>}
                {entry.drinks.length ? (
                  <ul>
                    {boardDrinkLines(entry.drinks).map((line) => (
                      <li key={line.key} className={line.move ? "move-line" : undefined}>{line.text}</li>
                    ))}
                  </ul>
                ) : (
                  <small>Ingen behövde dricka.</small>
                )}
              </article>
            ))
          )}
        </div>
      </section>

      <footer className="actionbar">
        <button className="plain-button" type="button" onClick={() => controller.leaveGame()}>
          Lämna
        </button>
        <button
          className="primary-button"
          type="button"
          disabled={!controller.canEndTurn()}
          onClick={() => controller.endTurn()}
        >
          {controller.endTurnLabel()}
        </button>
      </footer>
    </section>
  );
}
